import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { useAuth, AuthHookPayload } from '@ts-core/modules/auth/hooks';

import { UserFragment } from '../graphql/fragments';
import { getActiveSessionQuery, GetActiveSessionQueryResult, GetActiveSessionQueryVariables } from '../graphql/queries/getActiveSessionQuery';

import { User, StudentSession } from '../types';
import { acceptTermsAndConditionsMutation } from '../graphql/mutations/acceptTermsAndConditionsMutation';

export interface StudentAuthHookPayload extends AuthHookPayload<User> {
  onAcceptTermsAndConditions: () => void;

  acceptTermsAndConditionsLoading: boolean;
  acceptTermsAndConditionsError?: string;

  userId: string;

  getActiveSessionLoading: boolean;
  onGetActiveSession: (email: string, onCompleted: (session: StudentSession | null) => void) => void;
}

export default function useStudentAuth(): StudentAuthHookPayload {
  const { showErrorNotification } = useSnackbarNotifications();
  const authHookPayload = useAuth<User>({ UserFragment });
  const userId = authHookPayload.user?.id || '';
  const apolloClient = useApolloClient();
  const [getActiveSessionLoading, setGetActiveSessionLoading] = useState(false);

  /**
   * Get active session
   */
  const onGetActiveSession = useCallback(
    (email: string, onCompleted: (session: StudentSession | null) => void) => {
      setGetActiveSessionLoading(true);
      apolloClient
        .query<GetActiveSessionQueryResult, GetActiveSessionQueryVariables>({
          query: getActiveSessionQuery,
          variables: {
            email,
          },
        })
        .then(({ data }) => {
          onCompleted(data.session);
        })
        .catch(() => onCompleted(null))
        .finally(() => {
          setGetActiveSessionLoading(false);
        });
    },
    [apolloClient]
  );

  /**
   * Accept terms and conditions
   */
  const [acceptTermsAndConditions, { loading: acceptTermsAndConditionsLoading, error: acceptTermsAndConditionsError }] = useMutation(
    acceptTermsAndConditionsMutation,
    { onError: showErrorNotification, onCompleted: authHookPayload.refetchProfile }
  );
  const onAcceptTermsAndConditions = useCallback(() => {
    acceptTermsAndConditions();
  }, [acceptTermsAndConditions]);

  return {
    ...authHookPayload,
    getActiveSessionLoading,
    onGetActiveSession,

    userId,
    onAcceptTermsAndConditions,
    acceptTermsAndConditionsLoading,
    acceptTermsAndConditionsError: getErrorMessage({ error: acceptTermsAndConditionsError }),
  };
}
